/**
 * FollowUpAccessGate - Security gate for follow-up questionnaires.
 * 
 * This component:
 * 1. Parses session ID and token from URL
 * 2. Verifies token with backend
 * 3. Handles authentication redirect if needed
 * 4. Shows appropriate error states
 * 5. Only renders questionnaire after successful verification
 * 
 * SECURITY: This is the frontend enforcement of the follow-up access control.
 * No questionnaire content is rendered until backend verification succeeds.
 */
import React, { useState, useEffect } from 'react';
import followUpService from '../services/followUp';
import LoadingSpinner from './LoadingSpinner';
import ErrorCard from './ErrorCard';

// Status messages for each verification state
const STATUS_MESSAGES = {
    AUTH_REQUIRED: {
        title: 'Login Required',
        message: 'Please log in to access your follow-up questionnaire.',
        action: 'Login to Continue'
    },
    FORBIDDEN: {
        title: 'Access Denied',
        message: 'This follow-up link is not valid or you do not have access.',
        action: null
    },
    EXPIRED: {
        title: 'Link Expired',
        message: 'This follow-up link has expired. Please contact your healthcare provider for a new link.',
        action: null
    },
    USED: {
        title: 'Link Already Used',
        message: 'This follow-up link has already been used. Each link can only be used once.',
        action: null
    },
    NETWORK_ERROR: {
        title: 'Connection Error',
        message: 'Unable to verify your access. Please check your internet connection and try again.',
        action: 'Retry'
    }
};

// WT-161: the backend replaced this legacy questionnaire with a Follow-Up Program.
export const REPLACED_BY_PROGRAM_ERROR_CODE = 'REPLACED_BY_PROGRAM';

export function replacedByProgramMessage(brandName) {
    const contact = brandName ? `contact ${brandName}` : 'contact your care team';
    return {
        title: 'This follow-up has been replaced',
        message: 'This follow-up questionnaire has been replaced with a new Follow-Up Program questionnaire. '
            + `Please log in to your patient portal to complete your new follow-up. If you need help, ${contact}.`,
        action: null
    };
}

/**
 * FollowUpAccessGate component.
 * 
 * @param {Object} props
 * @param {string} props.sessionId - Follow-up session ID from URL
 * @param {string} props.token - Raw token from URL
 * @param {Function} props.onVerified - Callback when verification succeeds with { questionnaireId, prefillData }
 * @param {Object} props.clientConfig - Client configuration for branding
 */
function FollowUpAccessGate({ sessionId, token, onVerified, clientConfig }) {
    const [status, setStatus] = useState('VERIFYING');
    const [verifyResult, setVerifyResult] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        if (sessionId && token) {
            verifyAccess();
        } else {
            setStatus('FORBIDDEN');
            setVerifyResult({ error_code: 'MISSING_PARAMS' });
        }
    }, [sessionId, token, retryCount]);

    const verifyAccess = async () => {
        setStatus('VERIFYING');

        try {
            const result = await followUpService.verifyToken(sessionId, token);
            setVerifyResult(result);

            if (result.status === 'OK') {
                // Success! Get prefill data and notify parent
                setStatus('VERIFIED');

                try {
                    const startResult = await followUpService.startQuestionnaire();
                    onVerified({
                        questionnaireId: result.questionnaire_id,
                        prefillData: startResult.prefill_data,
                        sessionId: result.session_id
                    });
                } catch (startError) {
                    console.error('Failed to start questionnaire:', startError);
                    // Still verified, just no prefill data
                    onVerified({
                        questionnaireId: result.questionnaire_id,
                        prefillData: null,
                        sessionId: result.session_id
                    });
                }
            } else if (result.status === 'AUTH_REQUIRED') {
                setStatus('AUTH_REQUIRED');
            } else {
                setStatus(result.status || 'FORBIDDEN');
            }
        } catch (error) {
            console.error('Verification error:', error);
            setStatus('NETWORK_ERROR');
            setVerifyResult({ error_code: 'NETWORK_ERROR' });
        }
    };

    const handleRetry = () => {
        setRetryCount(prev => prev + 1);
    };

    const handleLogin = () => {
        if (verifyResult?.login_redirect_url) {
            // Redirect to patient portal login with return URL
            window.location.href = verifyResult.login_redirect_url;
        } else {
            // Fallback: construct login URL
            const patientPortalDomain = process.env.REACT_APP_PATIENT_PORTAL_URL || '';
            const returnPath = `/follow-up/${sessionId}?token=${encodeURIComponent(token)}`;
            window.location.href = `${patientPortalDomain}/auth/signin?return=${encodeURIComponent(returnPath)}`;
        }
    };

    // Verifying state - show spinner
    if (status === 'VERIFYING') {
        return (
            <div className="followup-gate" style={styles.container}>
                <div style={styles.card}>
                    <LoadingSpinner text="Verifying your access..." />
                    <p style={styles.subtext}>
                        Please wait while we verify your follow-up link.
                    </p>
                </div>
            </div>
        );
    }

    // Verified state - parent will render questionnaire
    if (status === 'VERIFIED') {
        return (
            <div className="followup-gate" style={styles.container}>
                <div style={styles.card}>
                    <LoadingSpinner text="Loading your questionnaire..." />
                </div>
            </div>
        );
    }

    // Error states
    const statusInfo = verifyResult?.error_code === REPLACED_BY_PROGRAM_ERROR_CODE
        ? replacedByProgramMessage(clientConfig?.name)
        : STATUS_MESSAGES[status] || STATUS_MESSAGES.FORBIDDEN;

    return (
        <div className="followup-gate" style={styles.container}>
            <div style={styles.card}>
                <div style={styles.iconContainer}>
                    {status === 'AUTH_REQUIRED' ? (
                        <svg style={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <circle cx="12" cy="16" r="1" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                    ) : status === 'EXPIRED' ? (
                        <svg style={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12,6 12,12 16,14" />
                        </svg>
                    ) : (
                        <svg style={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                    )}
                </div>

                <h2 style={styles.title}>{statusInfo.title}</h2>
                <p style={styles.message}>{statusInfo.message}</p>

                {statusInfo.action && (
                    <button
                        onClick={status === 'AUTH_REQUIRED' ? handleLogin : handleRetry}
                        style={styles.button}
                    >
                        {statusInfo.action}
                    </button>
                )}

                {status === 'FORBIDDEN' && verifyResult?.error_code === 'WRONG_PATIENT' && (
                    <p style={styles.hint}>
                        This follow-up is for a different account. Please log out and log in with the correct account.
                    </p>
                )}
            </div>
        </div>
    );
}

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'var(--zyvgo-background, #FAFAFA)',
        padding: '20px',
    },
    card: {
        background: 'white',
        borderRadius: '16px',
        padding: '48px 40px',
        maxWidth: '440px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    },
    iconContainer: {
        marginBottom: '24px',
    },
    icon: {
        width: '64px',
        height: '64px',
        color: 'var(--zyvgo-primary, #222222)',
    },
    title: {
        fontSize: '24px',
        fontWeight: '600',
        color: 'var(--zyvgo-text, #1a1a1a)',
        marginBottom: '12px',
        fontFamily: 'var(--zyvgo-font-family, inherit)',
    },
    message: {
        fontSize: '16px',
        color: 'var(--zyvgo-text-secondary, #666666)',
        marginBottom: '24px',
        lineHeight: '1.5',
    },
    button: {
        display: 'inline-block',
        padding: '14px 32px',
        backgroundColor: 'var(--zyvgo-primary, #222222)',
        color: 'white',
        border: 'none',
        borderRadius: '28px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    subtext: {
        fontSize: '14px',
        color: 'var(--zyvgo-text-secondary, #888888)',
        marginTop: '16px',
    },
    hint: {
        fontSize: '14px',
        color: 'var(--zyvgo-warning, #e67e22)',
        marginTop: '16px',
        padding: '12px',
        background: 'var(--zyvgo-warning-bg, #fff8e1)',
        borderRadius: '8px',
    },
};

export default FollowUpAccessGate;
